import React from 'react';
import { useFilters, useSortBy, useTable } from 'react-table';

import { useKeyboardNavigation, useTableSelectFirstRow } from '../../hooks';
import type { TableColumn, TableColumnInstance, TableSortRule, Tournament } from '../../types/MTT';

import styles from './Table.scss';
import TableBody from './TableBody/TableBody';
import TableHead from './TableHead/TableHead';
import defaultColumns from './columns';
import { DEFAULT_COLUMN } from './columnsConfig';

export interface TableProps {
  data: Tournament[];
  loading: boolean;
  selectedRowId: number;
  sortRule: TableSortRule;
  handleColumnClick(column: TableColumnInstance): void;
  handleRowClick(rowId: number): void;
  handleRowDoubleClick(rowId: number): void;
  dataTestId?: string;
  columns?: TableColumn[];
}

const Table: React.FC<TableProps> = ({
  data,
  selectedRowId,
  sortRule,
  loading,
  dataTestId,
  columns = defaultColumns,
  handleColumnClick,
  handleRowClick,
  handleRowDoubleClick
}) => {
  const { headerGroups, rows, prepareRow } = useTable<Tournament>(
    {
      columns,
      data,
      initialState: { sortBy: [sortRule] },
      defaultColumn: DEFAULT_COLUMN
    },
    useFilters,
    useSortBy
  );

  const availableRows = rows.filter((row) => row.original.isEnabled);
  const firstAvailableRowId = availableRows?.[0]?.original?.id;

  useKeyboardNavigation({
    items: availableRows,
    selectedItemId: selectedRowId,
    getItemId: (row) => row.original.id,
    onItemSelect: handleRowClick
  });

  useTableSelectFirstRow({
    firstRowId: firstAvailableRowId,
    selectedRowId,
    setSelectedTournamentId: handleRowClick
  });

  return (
    <div data-testid={dataTestId} className={styles.root}>
      <div className={styles.header}>
        <TableHead headerGroups={headerGroups} handleColumnClick={handleColumnClick} />
      </div>
      <TableBody
        height=""
        width=""
        className={styles.table}
        autoSizer
        itemSize={26}
        itemCount={rows?.length}
        itemData={{
          rows,
          prepareRow,
          onRowClick: handleRowClick,
          onRowDoubleCLick: handleRowDoubleClick,
          selectedRowId
        }}
        loading={loading}
      />
    </div>
  );
};

export default Table;
